/**
 * @module components/Layout/GlobalSidebar
 * @description Sidebar principal de navegación del sistema ACSGC.
 *
 * Renderiza la navegación global con íconos y etiquetas para todos los módulos
 * habilitados según `FEATURES` (feature flags). Los ítems deshabilitados se ocultan
 * completamente; no se muestran como grises para no confundir al usuario.
 *
 * ## Estructura del sidebar
 * 1. Logo ACSGC (fijo, parte superior)
 * 2. Navegación principal: Dashboard, Auditoría, Activos, Eventos, Reportes, Progreso, etc.
 * 3. Sección dinámica según framework activo:
 *    - ISO 27002: lista de controles con categorías colapsables, filtros, referentes
 *    - ISO 27001: lista de cláusulas (4–10) con requisitos
 *    - LMCS: títulos de la Ley Marco con artículos
 *    - CIS: controles CIS v8 con salvaguardas
 *
 * ## Comportamiento por framework
 * El sidebar escucha `useFramework()` y adapta la sección dinámica. La transición
 * entre frameworks es inmediata sin recarga de página.
 *
 * ## Feature flags
 * Cada ítem de navegación usa `isViewEnabled(viewId)` para decidir si renderiza.
 * Agregar un nuevo módulo: añadir entrada en `VIEW_FEATURE_MAP` en `config/features.ts`.
 *
 * @see config/features.ts para el mapa de vistas a flags
 * @see MainLayout para el consumidor que recibe `onViewChange`
 */

import React, { useState } from 'react';
import {
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  FileText,
  Filter,
  UserPlus,
  LayoutDashboard,
  ClipboardList,
  Package,
  Shield,
  ShieldCheck,
  AlertTriangle,
  FileBarChart,
  TrendingUp,
  MessageSquareMore
} from 'lucide-react';
import { ISOControl, SelectedControl } from '../../types/iso';
import { useData } from '../../contexts/DataContext';
import { useFramework } from '../../contexts/FrameworkContext';
import { FilterPanel } from '../Filters/FilterPanel';
import { TechnicalReferentsPanel } from '../TechnicalReferents/TechnicalReferentsPanel';

// =====================================================
// INTERFACES
// =====================================================

type ViewType = 'dashboard' | 'national-ra' | 'network-ra' | 'audit' | 'assets' | 'security-events' | 'reports' | 'progress' | 'acs' | 'cis';

interface GlobalSidebarProps {
  collapsed: boolean;
  onToggleSidebar: () => void;
  currentView: ViewType;
  onViewChange: (view: ViewType) => void;
  onSelectControl: (control: SelectedControl | null) => void;
  selectedControl: SelectedControl | null;
}

type SubTab = 'controls' | 'filters' | 'referents';

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

export function GlobalSidebar({
  collapsed,
  onToggleSidebar,
  currentView,
  onViewChange,
  onSelectControl,
  selectedControl
}: GlobalSidebarProps) {
  const { filteredControls, filter } = useData();
  const { activeFramework } = useFramework();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('controls');
  const [navExpanded, setNavExpanded] = useState<boolean>(true); // Estado para colapsar/expandir navegación

  // Calcular filtros activos
  const activeFiltersCount = React.useMemo(() => {
    return (
      filter.status.length +
      filter.priority.length +
      filter.category.length +
      filter.assignee.length +
      (filter.dateRange.start ? 1 : 0) +
      (filter.dateRange.end ? 1 : 0)
    );
  }, [filter]);

  // Agrupar controles por categoría
  const categories = filteredControls.reduce((acc, control) => {
    if (!acc[control.category]) {
      acc[control.category] = [];
    }
    acc[control.category].push(control);
    return acc;
  }, {} as Record<string, ISOControl[]>);

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const handleControlClick = (control: ISOControl) => {
    const selectedWithBreadcrumb: SelectedControl = {
      ...control,
      breadcrumb: [control.category, control.subcategory, control.title]
    };
    onSelectControl(selectedWithBreadcrumb);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-500';
      case 'in_progress': return 'bg-blue-500';
      case 'review': return 'bg-yellow-500';
      case 'not_started': return 'bg-gray-400';
      default: return 'bg-red-500';
    }
  };

  // Determinar si estamos en una vista de Red Asistencial (nacional o red)
  const isRAView = currentView === 'national-ra' || currentView === 'network-ra';

  // Determinar framework activo
  const isISO27001 = activeFramework?.code === 'ISO27001';
  const isLMCS = activeFramework?.code === 'LMCS';

  // Items de navegación principal - filtrar según vista y framework
  // showIn27001: visible en ISO 27001
  // showInLMCS: visible en Ley Marco Ciberseguridad
  // showIn27002: visible en ISO 27002 (default)
  const allNavItems = [
    { id: 'dashboard' as const, label: 'Dashboard', icon: LayoutDashboard, showInRA: true, showIn27001: true, showInLMCS: true, showIn27002: true },
    { id: 'audit' as const, label: 'Auditoría', icon: ClipboardList, showInRA: false, showIn27001: true, showInLMCS: true, showIn27002: true },
    { id: 'incidents' as const, label: 'Incidentes', icon: AlertTriangle, showInRA: false, showIn27001: false, showInLMCS: true, showIn27002: false },
    { id: 'assets' as const, label: 'Activos', icon: Package, showInRA: false, showIn27001: false, showInLMCS: false, showIn27002: true },
    { id: 'security-events' as const, label: 'Eventos de Seguridad', icon: AlertTriangle, showInRA: false, showIn27001: false, showInLMCS: false, showIn27002: true },
    { id: 'reports' as const, label: 'Reportes', icon: FileBarChart, showInRA: false, showIn27001: false, showInLMCS: false, showIn27002: true },
    { id: 'progress' as const, label: 'Progreso', icon: TrendingUp, showInRA: false, showIn27001: false, showInLMCS: false, showIn27002: true },
    { id: 'acs' as const, label: 'Sesiones ACS', icon: MessageSquareMore, showInRA: false, showIn27001: true, showInLMCS: true, showIn27002: true }
  ];

  // Filtrar items según framework activo
  const navItems = allNavItems.filter(item => {
    if (isRAView) return item.showInRA;
    if (isISO27001) return item.showIn27001;
    if (isLMCS) return item.showInLMCS;
    return item.showIn27002;
  });

  // =====================================================
  // VERSIÓN COLAPSADA
  // =====================================================

  if (collapsed) {
    return (
      <aside className="w-16 bg-white dark:bg-slate-900 border-r border-gray-200 dark:border-slate-700 flex flex-col items-center py-4 transition-all duration-300">
        {/* Logo pequeño */}
        <div className="mb-4">
          <img
            src="/acsgc-logo.png"
            alt="ACSGC"
            className="w-10 h-10 object-contain"
          />
        </div>

        <div className="w-10 h-px bg-gray-300 dark:bg-slate-700 mb-4" />

        {/* Navegación principal colapsada */}
        <nav className="flex flex-col items-center space-y-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            // Dashboard se marca activo si estamos en dashboard, national-ra o network-ra
            const isActive = item.id === 'dashboard'
              ? (currentView === 'dashboard' || currentView === 'national-ra' || currentView === 'network-ra')
              : currentView === item.id;
            return (
              <div key={item.id} className="relative group">
                <button
                  onClick={() => onViewChange(item.id)}
                  className={`p-2.5 rounded-lg transition-all ${
                    isActive
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white'
                  }`}
                  aria-label={item.label}
                >
                  <Icon className="w-5 h-5" />
                </button>
                {/* Tooltip */}
                <div className="absolute left-full ml-2 px-2 py-1 bg-gray-800 dark:bg-slate-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                  {item.label}
                </div>
              </div>
            );
          })}
        </nav>

        {/* Separador */}
        <div className="w-10 h-px bg-slate-700 my-4" />

        {/* Sub-navegación (solo en dashboard) */}
        {currentView === 'dashboard' && (
          <div className="flex flex-col items-center space-y-2">
            {[
              { id: 'controls', icon: FileText, label: 'Controles' },
              { id: 'filters', icon: Filter, label: 'Filtros' },
              { id: 'referents', icon: UserPlus, label: 'Referentes' }
            ].map((item) => {
              const Icon = item.icon;
              const isActive = activeSubTab === item.id;
              return (
                <div key={item.id} className="relative group">
                  <button
                    onClick={() => {
                      setActiveSubTab(item.id as SubTab);
                      onToggleSidebar(); // Expandir al hacer clic
                    }}
                    className={`p-2 rounded-lg transition-all ${
                      isActive
                        ? 'bg-slate-700 text-blue-400'
                        : 'text-gray-600 dark:text-slate-500 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-slate-300'
                    }`}
                    aria-label={item.label}
                  >
                    <Icon className="w-4 h-4" />
                  </button>
                  {/* Tooltip */}
                  <div className="absolute left-full ml-2 px-2 py-1 bg-gray-800 dark:bg-slate-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                    {item.label}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Botón de expandir */}
        <div className="mt-auto">
          <button
            onClick={onToggleSidebar}
            className="p-2 text-gray-500 dark:text-slate-500 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            aria-label="Expandir sidebar"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </aside>
    );
  }

  // =====================================================
  // VERSIÓN EXPANDIDA
  // =====================================================

  return (
    <aside className="w-72 bg-white dark:bg-slate-900 border-r border-gray-200 dark:border-slate-700 flex flex-col transition-all duration-300">
      {/* Header con Logo */}
      <div className="p-4 border-b border-gray-200 dark:border-slate-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img
              src="/acsgc-logo.png"
              alt="ACS"
              className="w-10 h-10 object-contain"
            />
            <div>
              <h1 className="text-gray-900 dark:text-white font-bold text-lg tracking-wide">ACS</h1>
              <p className="text-gray-500 dark:text-slate-500 text-xs">Global Core</p>
            </div>
          </div>
          <button
            onClick={onToggleSidebar}
            className="p-1.5 text-gray-500 dark:text-slate-500 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            aria-label="Colapsar sidebar"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Navegación Principal - Colapsable */}
      <nav className="border-b border-slate-800">
        {/* Header de Navegación - Colapsable */}
        <button
          onClick={() => setNavExpanded(!navExpanded)}
          className="w-full p-3 flex items-center justify-between hover:bg-gray-100 dark:hover:bg-slate-800/50 transition-colors"
          aria-label="Toggle navigation"
        >
          <div className="flex items-center gap-2">
            <LayoutDashboard className="w-4 h-4 text-gray-600 dark:text-slate-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-slate-300">Navegación</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600 dark:text-slate-400 bg-gray-200 dark:bg-slate-800 px-2 py-0.5 rounded-full">
              {navItems.length}
            </span>
            {navExpanded ? (
              <ChevronDown className="w-4 h-4 text-gray-500 dark:text-slate-500" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-500 dark:text-slate-500" />
            )}
          </div>
        </button>

        {/* Items de Navegación */}
        {navExpanded && (
          <div className="px-3 pb-3 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              // Dashboard se marca activo si estamos en dashboard, national-ra o network-ra
              const isActive = item.id === 'dashboard'
                ? (currentView === 'dashboard' || currentView === 'national-ra' || currentView === 'network-ra')
                : currentView === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => onViewChange(item.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                    isActive
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-600 dark:text-slate-400 hover:bg-gray-100 dark:hover:bg-slate-800 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </nav>

      {/* Sección del Framework Activo (solo en dashboard) */}
      {currentView === 'dashboard' && (
        <>
          {/* Título del Framework */}
          <div className="px-4 py-3 border-b border-slate-800">
            <div className="flex items-center gap-2">
              {isLMCS ? (
                <Shield className="w-4 h-4 text-red-400" />
              ) : isISO27001 ? (
                <ShieldCheck className="w-4 h-4 text-indigo-400" />
              ) : (
                <Shield className="w-4 h-4 text-blue-400" />
              )}
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {activeFramework?.name || 'ISO 27002'}
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">
              {isLMCS
                ? '15 artículos'
                : isISO27001
                  ? '30 requisitos'
                  : `${filteredControls.length} controles`}
            </p>
          </div>

          {/* Tabs de sub-navegación */}
          <div className="px-3 py-2 border-b border-slate-800">
            <div className="flex bg-gray-200 dark:bg-slate-800 rounded-lg p-1 gap-1">
              {[
                { id: 'controls', icon: FileText, label: isLMCS ? 'Artículos' : isISO27001 ? 'Requisitos' : 'Controles', count: filteredControls.length },
                { id: 'filters', icon: Filter, label: 'Filtros', count: activeFiltersCount },
                { id: 'referents', icon: UserPlus, label: 'Referentes', count: null }
              ].map((item) => {
                const Icon = item.icon;
                const isActive = activeSubTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveSubTab(item.id as SubTab)}
                    className={`flex-1 flex items-center justify-center gap-1 px-1.5 py-2 rounded-md text-xs font-medium transition-colors ${
                      isActive
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-slate-200'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">{item.label}</span>
                    {item.count !== null && item.count > 0 && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                        isActive
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-300 dark:bg-slate-700 text-gray-600 dark:text-slate-300'
                      }`}>
                        {item.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Contenido dinámico */}
          <div className="flex-1 overflow-auto">
            {activeSubTab === 'controls' ? (
              <div className="p-2">
                {Object.entries(categories).map(([category, controls]) => (
                  <div key={category} className="mb-1">
                    <button
                      onClick={() => toggleCategory(category)}
                      className="w-full flex items-center justify-between p-2 text-left rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors group"
                    >
                      <span className="text-sm font-medium text-gray-700 dark:text-slate-300 group-hover:text-gray-900 dark:group-hover:text-white truncate">
                        {category}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 dark:text-slate-500">
                          {controls.length}
                        </span>
                        {expandedCategories.has(category) ? (
                          <ChevronDown className="w-4 h-4 text-gray-500 dark:text-slate-500" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-500 dark:text-slate-500" />
                        )}
                      </div>
                    </button>

                    {expandedCategories.has(category) && (
                      <div className="ml-2 mt-1 space-y-0.5">
                        {controls.map((control) => (
                          <button
                            key={control.id}
                            onClick={() => handleControlClick(control)}
                            className={`w-full p-2 text-left rounded-lg transition-colors ${
                              selectedControl?.id === control.id
                                ? 'bg-blue-600/20 border border-blue-500/50'
                                : 'hover:bg-gray-100 dark:hover:bg-slate-800'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${getStatusColor(control.status)}`} />
                              <span className="text-xs font-mono text-blue-400">
                                {control.code}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-slate-300 mt-1 line-clamp-1">
                              {control.title}
                            </p>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : activeSubTab === 'filters' ? (
              <div className="p-2">
                <FilterPanel />
              </div>
            ) : (
              <div className="p-2">
                <TechnicalReferentsPanel />
              </div>
            )}
          </div>
        </>
      )}

      {/* Footer */}
      <div className="p-3 border-t border-gray-200 dark:border-slate-800">
        <p className="text-xs text-gray-400 dark:text-slate-600 text-center">
          Advanced Cyber Security Global Core
        </p>
      </div>
    </aside>
  );
}

export default GlobalSidebar;
